import { datadogRum } from '@datadog/browser-rum'
import { reactPlugin } from '@datadog/browser-rum-react'

import { getRegardWindowValue } from '~/shared/window'
import { getDatadogConfig, shouldBypassAuthForE2E } from '~/shell/config'

/** The Datadog service name for the suite; matches `tags.datadoghq.com/service` in the Helm chart. */
const DATADOG_SERVICE = 'atlas-frontend'

/**
 * Initializes Datadog RUM for error logging and frontend telemetry.
 *
 * Called once from `main.tsx` after runtime config loads and before render. RUM
 * is initialized only when `getDatadogConfig()` resolves both an application ID
 * and a client token, so local dev, the mock backend, and any deploy that leaves
 * the tokens blank are inert with no further gating. The end-to-end auth bypass
 * also suppresses init so system-test runs never emit RUM data.
 *
 * Session Replay masks all text and media (`defaultPrivacyLevel: 'mask'`) rather
 * than only inputs, because the suite renders patient PHI (names, MRNs,
 * conditions) as on-screen text that must not be captured in replays.
 *
 * Identity is intentionally minimal here: the service, env, and build version
 * are all that is known before React and Auth0 mount. The session user and the
 * active customer are attached later by the `DatadogIdentityBridge` once Auth0
 * resolves.
 */
export const initializeDatadog = (): void => {
  if (shouldBypassAuthForE2E) {
    return
  }

  const config = getDatadogConfig()
  if (!config) {
    return
  }

  const isDev = config.env === 'dev'

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: 'datadoghq.com',
    service: DATADOG_SERVICE,
    env: config.env,
    version: getRegardWindowValue('version'),
    sessionSampleRate: 100,
    sessionReplaySampleRate: isDev ? 0 : 5,
    sessionPersistence: 'local-storage',
    // Mask ALL text and media, not just inputs: the suite displays patient PHI
    // as rendered text, which 'mask-user-input' would leave readable in replays.
    defaultPrivacyLevel: 'mask',
    trackResources: !isDev,
    trackLongTasks: true,
    trackUserInteractions: true,
    useSecureSessionCookie: true,
    // Backend trace correlation (`allowedTracingUrls`) is deliberately omitted.
    // The suite calls the atlas service cross-origin, so injecting trace headers
    // requires the atlas service's CORS to allow `traceparent`/`tracestate`/`baggage`
    // and the atlas service itself to be APM-instrumented; until both are confirmed,
    // enabling it would break requests on a failed preflight. See
    // docs/adr/0004-datadog-rum-observability.md (RND-11809).
    //
    // Route changes create named RUM views via the react-router integration;
    // `App.tsx` must build its routers with the wrapped factories from
    // `@datadog/browser-rum-react/react-router-v7` for this to take effect.
    plugins: [reactPlugin({ router: true })],
    // Nginx and Vite proxy this path because direct calls to Datadog intake are commonly blocked by ad blockers.
    proxy: (options) => `/dd${options.path}?${options.parameters}`,
  })
}
